import { useQuery } from '@tanstack/react-query';
import {
  ContainerTradeData,
  LoadDischargeData,
  PortcallTimeliness,
  TradeByPartner,
  TradePortDetailInfo,
  Turnaround,
  FreightRateIndex,
  PartnerTradeTransit,
  LeadTimesNew,
  CountryTrade,
  PortContainerType,
  ContainersTerminal,
  DistributionCenter,
} from 'src/model/port';
import { TradeDataPort } from 'src/model/tradeData';
import { PortService, TradeDataService } from 'src/services/api';
import { FieldRenameMapper } from 'src/utils/helpers/dataMappers/dataMappers';
import { IGetListTradeDataPortPayload } from 'src/utils/typings/dto/tradeData';
import { ITradePortDetailInfo } from 'src/utils/typings/response';

const QUERY_KEY_LIST_PORT = 'QUERY_KEY_LIST_PORT';
const QUERY_KEY_CONTAINER_TRADE_CHART = 'QUERY_KEY_CONTAINER_TRADE_CHART';
const QUERY_KEY_LOAD_DISCHARGE_CHART = 'QUERY_KEY_LOAD_DISCHARGE_CHART';
const QUERY_KEY_TRADE_PARTNER_CHART = 'QUERY_KEY_TRADE_PARTNER_CHART';
const QUERY_KEY_TURNAROUND_CHART = 'QUERY_KEY_TURNAROUND_CHART';
const QUERY_KEY_PORTCALL_TIMELINESS_CHART = 'QUERY_KEY_PORTCALL_TIMELINESS_CHART';
const QUERY_KEY_FREIGHT_RATE_CHART = 'QUERY_KEY_FREIGHT_RATE_CHART';
const QUERY_KEY_PARTNER_TRADE_TRANSIT = 'QUERY_KEY_PARTNER_TRADE_TRANSIT';
const QUERY_KEY_LEAD_TIME_NEW = 'QUERY_KEY_LEAD_TIME_NEW';
const QUERY_KEY_COUNTRY_TRADE = 'QUERY_KEY_COUNTRY_TRADE';
const QUERY_KEY_CONTAINERS_TYPE = 'QUERY_KEY_CONTAINERS_TYPE';
const QUERY_KEY_CONTAINERS_TERMINAL = 'QUERY_KEY_CONTAINERS_TERMINAL';
const QUERY_KEY_DISTRIBUTION_CENTER = 'QUERY_KEY_DISTRIBUTION_CENTER';

export const useQueryListPort = (query: IGetListTradeDataPortPayload) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_LIST_PORT, query],
    queryFn: () => TradeDataService.getListPort(query),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new TradeDataPort(),
    placeholderData: (previousData) => previousData,
  });
  return {
    data,
    isFetching,
    refetch,
  };
};

export const useQueryPortDetail = (id: string) => {
  const { isLoading, error, data, isError, isFetching, isLoadingError, isPending, refetch } = useQuery({
    queryKey: [QUERY_KEY_LIST_PORT, id],
    queryFn: () => PortService.getPortDetail(id),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new TradePortDetailInfo(),
    placeholderData: (previousData) => previousData,
  });
  const dataMapping: ITradePortDetailInfo = FieldRenameMapper.formatObjectKeys(data?.data || {});

  return {
    isLoadingError,
    isFetching,
    isLoading,
    error,
    data: { ...data, data: dataMapping },
    isError,
    isPending,
    refetch,
  };
};

export const useQueryDataContainerTradeChart = ({ locode, freq }: { locode: string; freq: string }) => {
  const { isLoading, error, data, isError, isFetching, isLoadingError, isPending, refetch } = useQuery({
    queryKey: [QUERY_KEY_CONTAINER_TRADE_CHART, locode, freq],
    queryFn: () => PortService.getDataContainerTradeChart({ locode, freq }),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new ContainerTradeData(),
    placeholderData: (previousData) => previousData,
    enabled: Boolean(locode),
  });

  return {
    isLoadingError,
    isFetching,
    isLoading,
    error,
    data,
    isError,
    isPending,
    refetch,
  };
};

export const useQueryDataLoadDischargeChart = ({ locode }: { locode: string }) => {
  const { isLoading, error, data, isError, isFetching, isLoadingError, isPending, refetch } = useQuery({
    queryKey: [QUERY_KEY_LOAD_DISCHARGE_CHART, locode],
    queryFn: () => PortService.getDataLoadDischargeChart({ locode }),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new LoadDischargeData(),
    placeholderData: (previousData) => previousData,
    enabled: Boolean(locode),
  });

  return {
    isLoadingError,
    isFetching,
    isLoading,
    error,
    data,
    isError,
    isPending,
    refetch,
  };
};

export const useQueryDataTradeByPartnerChart = ({ locode, isExport }: { locode: string; isExport: number }) => {
  const { isLoading, error, data, isError, isFetching, isLoadingError, isPending, refetch } = useQuery({
    queryKey: [QUERY_KEY_TRADE_PARTNER_CHART, locode, isExport],
    queryFn: () => PortService.getDataTradeByPartner({ locode, isExport }),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new TradeByPartner(),
    placeholderData: (previousData) => previousData,
    enabled: Boolean(locode),
  });

  return {
    isLoadingError,
    isFetching,
    isLoading,
    error,
    data,
    isError,
    isPending,
    refetch,
  };
};

export const useQueryDataTurnaroundChart = ({ locode }: { locode: string }) => {
  const { isLoading, error, data, isError, isFetching, isLoadingError, isPending, refetch } = useQuery({
    queryKey: [QUERY_KEY_TURNAROUND_CHART, locode],
    queryFn: () => PortService.getDataTurnaround({ locode }),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new Turnaround(),
    placeholderData: (previousData) => previousData,
    enabled: Boolean(locode),
  });

  return {
    isLoadingError,
    isFetching,
    isLoading,
    error,
    data,
    isError,
    isPending,
    refetch,
  };
};

export const useQueryDataPortcallTimelinessChart = ({ locode }: { locode: string }) => {
  const { isLoading, error, data, isError, isFetching, isLoadingError, isPending, refetch } = useQuery({
    queryKey: [QUERY_KEY_PORTCALL_TIMELINESS_CHART, locode],
    queryFn: () => PortService.getDataPortcallTimeliness({ locode }),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new PortcallTimeliness(),
    placeholderData: (previousData) => previousData,
    enabled: Boolean(locode),
  });

  return {
    isLoadingError,
    isFetching,
    isLoading,
    error,
    data,
    isError,
    isPending,
    refetch,
  };
};

export const useQueryDataFreightRateIndexChart = ({
                                                    locode,
                                                    locodeDestination,
                                                  }: {
  locode: string;
  locodeDestination: string;
}) => {
  const { isLoading, error, data, isError, isFetching, isLoadingError, isPending, refetch } = useQuery({
    queryKey: [QUERY_KEY_FREIGHT_RATE_CHART, locode, locodeDestination],
    queryFn: () => PortService.getDataFreightRateIndex({ locode, locodeDestination }),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new FreightRateIndex(),
    placeholderData: (previousData) => previousData,
    enabled: Boolean(locode) && Boolean(locodeDestination),
  });
  return {
    isLoadingError,
    isFetching,
    isLoading,
    error,
    data,
    isError,
    isPending,
    refetch,
  };
};
export const useQueryDataContainerType = ({ locode }: { locode: string }) => {
  const { isLoading, error, data, isError, isFetching, isLoadingError, isPending, refetch } = useQuery({
    queryKey: [QUERY_KEY_CONTAINERS_TYPE, locode],
    queryFn: () => PortService.getDataContainerType({ locode }),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new PortContainerType(),
    placeholderData: (previousData) => previousData,
    enabled: Boolean(locode),
  });

  return {
    isLoadingError,
    isFetching,
    isLoading,
    error,
    data,
    isError,
    isPending,
    refetch,
  };
};

export const useQueryDataPartnerTradeTransit = ({ page, pageSize }: { page: number; pageSize: number }) => {
  const { isLoading, error, data, isError, isFetching, isLoadingError, isPending, refetch } = useQuery({
    queryKey: [QUERY_KEY_PARTNER_TRADE_TRANSIT, page, pageSize],
    queryFn: () => PortService.getDataPartnerTradeTransit({ page, pageSize }),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new PartnerTradeTransit(),
    placeholderData: (previousData) => previousData,
  });

  return {
    isLoadingError,
    isFetching,
    isLoading,
    error,
    data,
    isError,
    isPending,
    refetch,
  };
};

export const useQueryDataContainersTerminal = ({ locode }: { locode: string }) => {
  const { isLoading, error, data, isError, isFetching, isLoadingError, isPending, refetch } = useQuery({
    queryKey: [QUERY_KEY_CONTAINERS_TERMINAL, locode],
    queryFn: () => PortService.getDataContainersTerminal({ locode }),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new ContainersTerminal(),
    placeholderData: (previousData) => previousData,
    enabled: Boolean(locode),
  });

  return {
    isLoadingError,
    isFetching,
    isLoading,
    error,
    data,
    isError,
    isPending,
    refetch,
  };
};

export const useQueryDataLeadTimesNew = ({
                                           locode,
                                           locodePartner,
                                           isExport,
                                         }: {
  locode: string;
  locodePartner: string;
  isExport: number;
}) => {
  const { isLoading, error, data, isError, isFetching, isLoadingError, isPending, refetch } = useQuery({
    queryKey: [QUERY_KEY_LEAD_TIME_NEW, locode, locodePartner, isExport],
    queryFn: () => PortService.getDataLeadTimesNew({ locode, locodePartner, isExport }),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new LeadTimesNew(),
    placeholderData: (previousData) => previousData,
    enabled: Boolean(locode) && Boolean(locodePartner),
  });

  return {
    isLoadingError,
    isFetching,
    isLoading,
    error,
    data,
    isError,
    isPending,
    refetch,
  };
};

export const useQueryDataCountryTrade = ({
                                           locode,
                                           locodePartner,
                                           freq,
                                           isExport,
                                         }: {
  locode: string;
  locodePartner: string;
  freq: string;
  isExport: number;
}) => {
  const { isLoading, error, data, isError, isFetching, isLoadingError, isPending, refetch } = useQuery({
    queryKey: [QUERY_KEY_COUNTRY_TRADE, locode, locodePartner, isExport, freq],
    queryFn: () => PortService.getDataCountryTrade({ locode, locodePartner, isExport, freq }),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new CountryTrade(),
    placeholderData: (previousData) => previousData,
    enabled: Boolean(locode) && Boolean(locodePartner),
  });

  return {
    isLoadingError,
    isFetching,
    isLoading,
    error,
    data,
    isError,
    isPending,
    refetch,
  };
};
export const useQueryDataDistributionCenter = ({ locode, isLast }: { locode: string; isLast: number }) => {
  const { isLoading, error, data, isError, isFetching, isLoadingError, isPending, refetch } = useQuery({
    queryKey: [QUERY_KEY_DISTRIBUTION_CENTER, locode, isLast],
    queryFn: () => PortService.getDataDistributionCenter({ locode, isLast }),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new DistributionCenter(),
    placeholderData: (previousData) => previousData,
    enabled: Boolean(locode),
  });

  return {
    isLoadingError,
    isFetching,
    isLoading,
    error,
    data,
    isError,
    isPending,
    refetch,
  };
};


